import * as React from 'react';

import { AppLink } from '@/shared/components/AppLink';
import { useAnalytics } from '../hooks/useAnalytics';

function isExternalLink(url: string) {
  return /^https?:\/\//i.test(url);
}

export interface AnalyticsBannerLinkProps {
  to: string;
  shouldRefresh?: boolean;
  bannerName: string;
  bannerType: string; // "banner" | "collections"
  fromNavBar?: boolean;
  sectionName?: string;
  /**
   * Experiment variant identifier (e.g. "A", "B")
   * This will be attached to analytics events as "Experiment Variant".
   */
  experimentVariant?: string;
  /**
   * Override the default click event name (e.g. "Homepage Hero Video Clicked").
   */
  eventName?: string;
  /**
   * Override or extend the default click event properties.
   */
  eventProperties?: Record<string, string | number | boolean | null | undefined>;
  className?: string;
  title?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  rel?: string;
  target?: string;
  'aria-label'?: string;
}

export function AnalyticsBannerLink({
  to,
  shouldRefresh,
  bannerName,
  bannerType,
  fromNavBar = false,
  sectionName,
  experimentVariant,
  eventName: eventNameOverride,
  eventProperties: eventPropertiesOverride,
  className,
  title,
  children,
  onClick,
  onMouseEnter,
  rel,
  target,
  'aria-label': ariaLabel,
}: AnalyticsBannerLinkProps) {
  const { analytics, isInitialized } = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Track only when a real destination exists
    if (to && to !== '#' && analytics && isInitialized) {
      try {
        const el = e.currentTarget as unknown as HTMLElement;
        const inferredSectionName =
          typeof el?.closest === 'function'
            ? el.closest('[data-analytics-section-name]')?.getAttribute('data-analytics-section-name') ?? undefined
            : undefined;
        const resolvedSectionName = sectionName ?? inferredSectionName;

        const eventName = eventNameOverride ?? 'Banner Clicked';
        const eventProperties = eventPropertiesOverride ?? {
          'Banner Name': bannerName,
          'Banner Type': bannerType,
          ...(resolvedSectionName ? { 'Section Name': resolvedSectionName } : {}),
          'From Nav Bar': fromNavBar,
          ...(experimentVariant ? { 'Experiment Variant': experimentVariant } : {}),
        };

        analytics.trackEvent(eventName, eventProperties);
      } catch (error) {
        console.error('Error tracking banner click:', error);
      }
    }

    onClick?.(e);
  };

  const linkProps = {
    className,
    title,
    'aria-label': ariaLabel,
    onClick: handleClick,
    onMouseEnter,
  };

  if (isExternalLink(to)) {
    return (
      <a
        href={to}
        {...linkProps}
        rel={rel ?? 'noopener noreferrer'}
        target={target}
      >
        {children}
      </a>
    );
  }

  // Internal links: trust the CMS `shouldRefresh` flag directly.
  // AppLink itself already defaults `shouldRefresh` to false (SPA nav).
  return (
    <AppLink to={to} shouldRefresh={shouldRefresh} {...linkProps}>
      {children}
    </AppLink>
  );
}

